import { Flex, Text, Transition } from '@mantine/core';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { StepProps } from '../types';
import {
  GenerateSuitabilityReportPayload,
  useGenerateSuitabilityReport,
} from '../hooks/useGenerateSuitabilityReport';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { getClientId } from '../edit/utils/getClientId';

type SequenceItem = { title: string; subtitle: string };

export function ProcessingStep({ data }: StepProps) {
  const navigate = useNavigate();
  const { generateSuitabilityReport } = useGenerateSuitabilityReport();

  const clientId = getClientId(data);

  const sequence = useMemo<SequenceItem[]>(
    () => [
      { title: 'Putting all together', subtitle: 'Pulling the supporting documentation' },
      { title: 'Fine tuning', subtitle: 'Analyzing provided information & documents' },
      { title: 'Final preparations', subtitle: 'Getting things ready...' },
      { title: 'All set', subtitle: 'Launching generating modules...' },
    ],
    [],
  );

  const [index, setIndex] = useState(0);
  const [mounted, setMounted] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sequenceDone, setSequenceDone] = useState(false);

  const didSubmitRef = useRef(false);

  const submitReport = async () => {
    if (!clientId || !data.template) return;

    setIsSubmitting(true);
    try {
      const payload: GenerateSuitabilityReportPayload = {
        reportTemplate: data.template.id,
        supportingDataUploadFileIds: data.adviceFiles.map(f => f.id),
        feeDocumentFileIds: data.feeDocuments.map(f => f.id),
        useDeepThinking: data.optimiseAdviceReadability,
        reportFormat: data.reportFormat,
        appendixFileIds: data.supportDocuments.map(u => u.fileId),
      };

      await generateSuitabilityReport(clientId, payload);
    } catch (error) {
      notifyAndLogError('Failed to generate report', 'Please try again.', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!clientId || didSubmitRef.current) return;
    didSubmitRef.current = true;
    void submitReport();
  }, [clientId]);

  useEffect(() => {
    if (!clientId || sequenceDone) return;

    const isLast = index >= sequence.length - 1;

    const showTimer = window.setTimeout(() => setMounted(false), 2000);

    const outTimer = window.setTimeout(() => {
      if (isLast) {
        setSequenceDone(true);
        return;
      }

      setIndex(i => i + 1);
      setMounted(true);
    }, 2000 + 250);

    return () => {
      window.clearTimeout(showTimer);
      window.clearTimeout(outTimer);
    };
  }, [index, sequence.length, clientId, sequenceDone]);

  useEffect(() => {
    if (!sequenceDone) return;
    if (isSubmitting) return;
    if (!clientId) return;

    navigate(`/client/${clientId}/document/suitability-reports`);
  }, [sequenceDone, isSubmitting, clientId, navigate]);

  const current = sequence[Math.min(index, sequence.length - 1)];

  return (
    <Flex direction="column" h="100%" justify="center" align="center" gap="xs">
      <Transition mounted={mounted} transition="fade-up" duration={250} timingFunction="ease">
        {styles => (
          <Flex style={styles} direction="column" align="center" gap="xxxs">
            <Text c="gray.10" fw={FONT_WEIGHTS.bold} size="displayMd">
              {current.title}
            </Text>
            <Text>{current.subtitle}</Text>
          </Flex>
        )}
      </Transition>
    </Flex>
  );
}
